import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { healthCyclesAPI } from '../../services/api'
import { errorCode } from './cycleStatus'

export const cyclePath = (cycleId) => `/health-cycle/cycles/${cycleId}`

/**
 * VH-227: start a cycle and open it. A 409 `draft_exists` is surfaced as
 * `conflict` so the caller can offer "open the draft" or "discard and restart";
 * `cycle_exists` (already finalized) simply opens the existing cycle.
 */
export function useStartHealthCycle() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [conflict, setConflict] = useState(null)

  const mutation = useMutation({
    mutationFn: (args) => healthCyclesAPI.start(args),
    onSuccess: (response) => {
      setConflict(null)
      queryClient.invalidateQueries({ queryKey: ['healthCycles'] })
      navigate(cyclePath(response.data.id))
    },
    onError: (err, args) => {
      const code = errorCode(err)
      const cycleId = err?.response?.data?.cycle_id
      if (code === 'cycle_exists' && cycleId) navigate(cyclePath(cycleId))
      else if (code === 'draft_exists' && cycleId) setConflict({ cycleId, args })
    },
  })

  return {
    start: (args) => mutation.mutate(args),
    discardAndRestart: () => conflict && mutation.mutate({ ...conflict.args, discardExistingDraft: true }),
    openExisting: () => conflict && navigate(cyclePath(conflict.cycleId)),
    dismissConflict: () => setConflict(null),
    conflict,
    isPending: mutation.isPending,
    error: conflict ? null : errorCode(mutation.error) || (mutation.error ? 'generic' : null),
  }
}
